import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import TeenGuardiansBox from "./cards/TeenGuardiansBox";
import TeenBusinessApplicationsBox from "./cards/TeenBusinessApplicationsBox";
import TeenRequestedGigsBox from "./cards/TeenRequestedGigsBox";
import TeenUpcomingGigsBox from "./cards/TeenUpcomingGigsBox";
import TeenReviewsBox from "./cards/TeenReviewsBox.tsx";
import teenDashboardBackground from "../../../assets/dashboard/upper_body_worker_briefcase_thinner.svg";
import { teenDashboardApi } from "../../../lib/teenDashboard";
import type { TeenDashboardSnapshotResponse } from "../../../types/teenDashboardTypes";
import { buildGuardianSetupHref, requiresGuardianForTeen } from "../../../utils/GuardianSetupUtils";
import DashboardNotificationsPreview from "../../dashboard/DashboardNotificationsPreview";

const TeenDashboardView = () => {
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState<TeenDashboardSnapshotResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadSnapshot = async () => {
      try {
        setLoading(true);
        setErrorMessage(null);
        const response = await teenDashboardApi.getSnapshot();
        if (!mounted) {
          return;
        }
        setSnapshot(response);
      } catch {
        if (!mounted) {
          return;
        }
        setErrorMessage("We couldn't load your dashboard right now.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadSnapshot();

    return () => {
      mounted = false;
    };
  }, []);

  const requestedGigs = snapshot?.requestedGigs ?? [];
  const upcomingConfirmedGigs = snapshot?.upcomingConfirmedGigs ?? [];
  const completedGigs = snapshot?.completedGigs ?? [];
  const reviews = snapshot?.reviews ?? [];
  const guardians = snapshot?.guardians ?? [];
  const requiresGuardian = Boolean(user && requiresGuardianForTeen(user.dateOfBirth));
  const activeGuardianCount = guardians.filter((guardian) => guardian.status === "ACTIVE").length;
  const pendingGuardianCount = guardians.filter((guardian) => guardian.status === "PENDING").length;
  const requiresGuardianSetup = requiresGuardian && activeGuardianCount === 0;
  const guardianSetupHref = buildGuardianSetupHref("/gigs");
  const firstName = user?.firstName?.trim() || null;

  return (
    <section className="mx-[calc(50%_-_50vw)] mt-2 w-screen space-y-4 px-2 sm:mx-0 sm:mt-4 sm:w-auto sm:space-y-6 sm:px-0 md:mt-5">
      <div className="space-y-2 sm:space-y-4">
        <section className="relative overflow-hidden rounded-xl border border-cyan-100 bg-gradient-to-r from-white via-cyan-50 to-emerald-50 p-3 shadow-sm sm:p-5">
          <img
            src={teenDashboardBackground}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 right-0 h-full max-h-56 w-[82%] object-contain object-right-bottom opacity-32 sm:right-4 sm:h-[90%] sm:w-[42%] sm:opacity-90 lg:right-6 lg:w-[38%]"
          />
          <div className="relative z-10 grid gap-3 sm:min-h-40 sm:max-w-[60%] sm:content-center sm:gap-4 lg:max-w-[58%]">
            <div className="min-w-0 text-left">
              {firstName ? <p className="text-sm font-semibold text-myteengig-primary">Hi {firstName}!</p> : null}
              <h2 className="mt-1 text-2xl font-bold leading-8 text-myteengig-text-primary">Teen Dashboard</h2>
              <p className="mt-2 hidden max-w-2xl text-sm leading-6 text-myteengig-text-muted sm:block">
                Stay on top of your upcoming gigs, reviews, completed work, and guardian connections.
              </p>
            </div>
            <Link
              to={requiresGuardianSetup ? guardianSetupHref : "/gigs"}
              className="inline-flex min-h-12 w-full shrink-0 items-center justify-center rounded-md bg-myteengig-primary px-3 py-3 text-base font-semibold text-white shadow-xs transition duration-200 ease-in-out hover:bg-myteengig-primary-hover hover:text-myteengig-secondary focus:ring-2 focus:ring-myteengig-primary focus:ring-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-myteengig-primary sm:w-72"
            >
              {requiresGuardianSetup ? "Set Up Parent/Guardian" : "Find a Gig"}
            </Link>
          </div>
        </section>

        <DashboardNotificationsPreview />
      </div>

      {errorMessage ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{errorMessage}</div>
      ) : null}

      {requiresGuardianSetup ? (
        <section className="rounded-[1.75rem] border border-amber-200 bg-amber-50 px-6 py-6 shadow-sm">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-amber-800">Before You Apply</p>
          <div className="mt-3 flex flex-col items-center gap-4 text-center">
            <div className="max-w-3xl">
              <h3 className="text-2xl font-semibold tracking-tight text-amber-950">Add a parent or guardian first</h3>
              <p className="mt-2 text-sm leading-6 text-amber-900/85">
                Teens under 18 need an active parent or guardian connected before applying to gigs.
                {pendingGuardianCount > 0
                  ? " You already have a pending request out. Once it is accepted, gig applications will unlock."
                  : " Send a guardian request now so you are ready when you find a gig you want."}
              </p>
            </div>
            <Link
              to={guardianSetupHref}
              className="inline-flex w-full items-center justify-center rounded-xl border border-amber-300 bg-white px-6 py-3 text-sm font-semibold text-amber-900 shadow-sm transition hover:bg-amber-100 sm:min-w-[340px] sm:w-auto sm:px-8"
            >
              {pendingGuardianCount > 0 ? "View guardian setup" : "Add parent/guardian"}
            </Link>
          </div>
        </section>
      ) : null}

      <div className="grid gap-3 sm:gap-6 lg:grid-cols-2">
        <TeenUpcomingGigsBox gigs={upcomingConfirmedGigs} loading={loading} />
        <TeenRequestedGigsBox gigs={requestedGigs} loading={loading} />
      </div>

      <div className="grid gap-3 sm:gap-6 lg:grid-cols-2">
        <TeenBusinessApplicationsBox />
        <TeenReviewsBox reviews={reviews} completedGigs={completedGigs} loading={loading} className="h-full" />
        <TeenGuardiansBox guardians={guardians} loading={loading} />
      </div>
    </section>
  );
};

export default TeenDashboardView;
